import React from 'react';
import { AVAILABLE_CULTURES, AVAILABLE_CURRENCIES } from '../../../../utilities/region';
import type { SettingsProfileFormProps } from './SettingsProfileForm.types';
import { useSettingsProfileForm } from './SettingsProfileForm.hook';
import * as styles from './SettingsProfileForm.styles';

export const SettingsProfileForm: React.FC<SettingsProfileFormProps> = (props) => {
  const {
    t,
    firstName, setFirstName,
    lastName, setLastName,
    name, setName,
    email, setEmail,
    phone, setPhone,
    dob, setDob,
    gender, setGender,
    culture, setCulture,
    currency, setCurrency,
    updatingProfile,
    handleUpdateProfile,
  } = useSettingsProfileForm(props);

  return (
    <form onSubmit={handleUpdateProfile} className={styles.formContainer}>
      <div className={styles.headerContainer}>
        <h3 className={styles.headerTitle}>{t('profileInformation', 'Profile Information')}</h3>
        <p className={styles.headerSubtitle}>{t('profileInformationSubtitle', 'Update your personal details and location settings.')}</p>
      </div>

      <div className={styles.gridContainer}>
        <div className={styles.inputGroup}>
          <label htmlFor="first-name-input" className={styles.label}>{t('firstName', 'First Name')}</label>
          <input
            id="first-name-input"
            data-testid="first-name-input"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={styles.inputClass}
          />
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="last-name-input" className={styles.label}>{t('lastName', 'Last Name')}</label>
          <input
            id="last-name-input"
            data-testid="last-name-input"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={styles.inputClass}
          />
        </div>
      </div>

      <div className={styles.gridContainer}>
        <div className={styles.inputGroup}>
          <label htmlFor="name-input" className={styles.label}>{t('name', 'Display Name')}</label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.inputClass}
            required
            data-testid="name-input"
          />
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="email-input" className={styles.label}>{t('email', 'Email Address')}</label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={styles.inputClass}
            required
            data-testid="email-input"
          />
        </div>
      </div>

      <div className={styles.gridContainer}>
        <div className={styles.inputGroup}>
          <label htmlFor="phone-input" className={styles.label}>{t('phone', 'Phone Number')}</label>
          <input
            id="phone-input"
            data-testid="phone-input"
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={styles.inputClass}
          />
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="dob-input" className={styles.label}>{t('dob', 'Date of Birth')}</label>
          <input
            id="dob-input"
            data-testid="dob-input"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            className={styles.inputClass}
          />
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="gender-select" className={styles.label}>{t('gender', 'Gender')}</label>
        <select
          id="gender-select"
          data-testid="gender-select"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          className={styles.selectClass}
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
          <option value="Prefer not to say">Prefer not to say</option>
        </select>
      </div>

      <div className={styles.gridContainer}>
        <div className={styles.inputGroup}>
          <label htmlFor="culture-select" className={styles.label}>{t('culture', 'Culture / Language')}</label>
          <select
            id="culture-select"
            value={culture}
            onChange={(e) => setCulture(e.target.value)}
            className={styles.selectClass}
            data-testid="culture-select"
          >
            <option value="">Select Culture</option>
            {AVAILABLE_CULTURES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor="currency-select" className={styles.label}>{t('currency', 'Currency')}</label>
          <select
            id="currency-select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={styles.selectClass}
            data-testid="currency-select"
          >
            <option value="">Select Currency</option>
            {AVAILABLE_CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.footerContainer}>
        <button
          type="submit"
          disabled={updatingProfile}
          className={styles.submitButton}
          data-testid="update-profile-btn"
        >
          {updatingProfile ? <i className={styles.spinnerIcon} /> : null}
          {t('saveChanges', 'Save Changes')}
        </button>
      </div>
    </form>
  );
};

export default SettingsProfileForm;
